import { h, Component } from 'preact';
import { Router } from 'preact-router';
import style from './style.css';
import Sites from '../../routes/sites';
import Site from '../../routes/site';
import AddDevice from '../../routes/add-device';
import Account from '../../routes/account';
import Subscribe from '../../routes/subscribe';
import Login from '../../routes/login';
import Reports from '../../routes/reports';
import Settings from '../../routes/settings';
import GlobalConfig from '../../routes/global-config';
import Keg from '../../routes/keg';
import UntappdCb from '../../routes/untappd-cb';
import SquareCb from '../../routes/square-callback';
import LightspeedCb from '../../routes/lightspeed-callback';
import PosMapping from '../../routes/pos-mapping';
import GetApp from '../../routes/get-app';
import ErrorBoundary from '../error-boundary';
import ChunkErrorNotice from '../chunk-error-notice';
import { clearChunkFailure, markRouteMounted } from '../../utils/chunkReload';

/** True for a direct child of the content area that a route rendered. */
const isRouteElement = (node) =>
    node && node.nodeType === 1 && !node.hasAttribute('data-chunk-error-notice');

export default class Content extends Component {
    /** Gets fired when the route changes.
	 *	@param {Object} event		"change" event from [preact-router](http://git.io/preact-router)
	 *	@param {string} event.url	The newly routed URL
	 */
    handleRoute = e => {
        this.currentUrl = e.url;
        // The next route may load fine; its own failure re-publishes.
        clearChunkFailure();
    };

    setRoot = (el) => {
        this.root = el;
    };

    componentDidMount() {
        this.observeFirstRouteMount();
    }

    componentWillUnmount() {
        this.stopObserving();
    }

    /**
     * Every route is a lazy chunk mounted by preact-cli's async loader, which
     * exposes no "loaded" signal — but a route that loaded puts an element in
     * the content area, and one whose chunk failed puts nothing. Watch for the
     * first route element so a recovery reload that landed on a new build can
     * retire its sessionStorage record (utils/chunkReload.js markRouteMounted).
     * Housekeeping only: whether the next failure reloads does not depend on it.
     */
    observeFirstRouteMount() {
        if (!this.root || typeof MutationObserver === 'undefined') return;
        const check = () => {
            if (!Array.from(this.root.children).some(isRouteElement)) return;
            markRouteMounted();
            this.stopObserving();
        };
        this.observer = new MutationObserver(check);
        this.observer.observe(this.root, { childList: true });
        check();
    }

    stopObserving() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    render() {
        let self = this;

        return (
            <div className={style.content} ref={self.setRoot}>
                <ChunkErrorNotice />
                <ErrorBoundary>
                    <Router onChange={self.handleRoute}>
                        <Login app={self.props.app} path="/" />
                        <Sites app={self.props.app} path="/sites" />
                        <Site app={self.props.app} path="/sites/:name" />
                        <AddDevice app={self.props.app} path="/add-device" />
                        <Reports app={self.props.app} path="/reports" />
                        <Account app={self.props.app} path="/account" />
                        <Subscribe app={self.props.app} path="/subscribe" />
                        <Settings app={self.props.app} path="/config/:name/:id/:port" />
                        <GlobalConfig app={self.props.app} path="/global-config/:name" />
                        <Keg app={self.props.app} path="/sites/:name/:id/:port" />
                        <UntappdCb app={self.props.app} path="/untappd-callback" />
                        <SquareCb app={self.props.app} path="/square-callback" />
                        <LightspeedCb app={self.props.app} path="/lightspeed-callback" />
                        <PosMapping app={self.props.app} path="/pos/:name" />
                        <GetApp app={self.props.app} path="/app" />
                        <GetApp app={self.props.app} path="/app/:rest*" />
                    </Router>
                </ErrorBoundary>
            </div>
        );
    }
}
